import { track, trackFeatureUsed } from '@/lib/analytics'
import { desktopSecureStorage, getPlatform, isTauri } from '@/lib/utils/tauri'
import { useOrganizationStore } from '@/store/organization.store'
import { env } from '@/utils/env'

import type {
  AssistantActionPlan,
  AssistantActionPlanCreated,
  AssistantActionPlanExecution,
  AssistantChatRequest,
  AssistantChatResponse,
  AssistantChatStreamEvent,
  AssistantMessagesPage,
  AssistantSession,
  AssistantSessionSearchResult,
} from '@scrambl/shared'

import { apiClient } from './api-client'
import { createSingletonApi } from './singleton-factory'

// Coarse buckets keep the property low-cardinality; the message text itself
// never leaves the client as an analytics property.
function bucketMessageLength(message: string): string {
  const length = message.trim().length
  if (length <= 40) return 'short'
  if (length <= 200) return 'medium'
  return 'long'
}

/**
 * The wire contract now lives in `@scrambl/shared` so mobile reads the same
 * one. Re-exported here because every web import goes through this module.
 */
export type {
  AICreditStatus,
  AssistantActionPlan,
  AssistantActionPlanCreated,
  AssistantActionPlanExecution,
  AssistantConfirmation,
  AssistantConfirmationItem,
  AssistantPlanCandidate,
  AssistantPlanReference,
  AssistantResolvedReference,
  AssistantActionPlanStep,
  AssistantChatAttachment,
  AssistantChatMessage,
  AssistantChatRequest,
  AssistantChatResponse,
  AssistantChatStreamEvent,
  AssistantMessagesPage,
  AssistantMessageAttachment,
  AssistantSession,
  AssistantSessionSearchResult,
  BennyErrorCode,
  SessionAnchor,
} from '@scrambl/shared'

/** How long the stream may go silent before the turn is ended locally. */
export const STREAM_IDLE_TIMEOUT_MS = 90_000

export class StreamIdleTimeoutError extends Error {
  constructor() {
    super('No data from Benny for 90 seconds')
    this.name = 'StreamIdleTimeoutError'
  }
}

/** `reader.read()` that rejects with StreamIdleTimeoutError when nothing arrives in time. */
async function readWithIdleTimeout<T>(
  reader: { read: () => Promise<ReadableStreamReadResult<T>> },
  timeoutMs = STREAM_IDLE_TIMEOUT_MS,
): Promise<ReadableStreamReadResult<T>> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      reader.read(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new StreamIdleTimeoutError()), timeoutMs)
      }),
    ])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

/** SSE stream handlers shared by chat and the confirm resume. */
interface StreamHandlers {
  signal: AbortSignal
  onEvent: (event: AssistantChatStreamEvent) => void
}

class AssistantApi {
  /**
   * Build a plan. `sessionId` is the conversation the plan card is written
   * into (created when absent); the response names it so the caller can adopt
   * a brand-new one.
   */
  async createActionPlan(request: {
    instruction: string
    context?: Record<string, unknown>
    includeAttention?: boolean
    sessionId?: string
  }): Promise<AssistantActionPlanCreated> {
    const response = await apiClient<{ data: AssistantActionPlanCreated }>(
      '/ai/assistant/action-plans',
      { method: 'POST', body: request },
    )
    trackFeatureUsed('benny_action_plan', {
      step_count: response.data?.steps?.length ?? 0,
    })
    return response.data
  }

  async getActionPlan(planId: string): Promise<AssistantActionPlan> {
    const response = await apiClient<{ data: AssistantActionPlan }>(
      `/ai/assistant/action-plans/${planId}`,
    )
    return response.data
  }

  /**
   * Answer a confirmation card. Approve runs the held writes and Benny
   * finishes the turn; decline records the refusal. Same event stream as
   * `chatStream`, so one reducer handles both.
   */
  async confirmToolCalls(
    sessionId: string,
    body: { toolCallIds: string[]; approve: boolean },
    handlers: StreamHandlers,
  ): Promise<void> {
    await this.streamEvents(
      `/ai/assistant/sessions/${sessionId}/confirm`,
      body,
      handlers,
    )
  }

  async executeActionPlan(
    planId: string,
  ): Promise<AssistantActionPlanExecution> {
    const response = await apiClient<{ data: AssistantActionPlanExecution }>(
      `/ai/assistant/action-plans/${planId}/execute`,
      { method: 'POST' },
    )
    return response.data
  }

  async chat(request: AssistantChatRequest): Promise<AssistantChatResponse> {
    const response = await apiClient<{ data: AssistantChatResponse }>(
      '/ai/assistant/chat',
      {
        method: 'POST',
        body: request,
      },
    )
    return response.data
  }

  /**
   * Stream a chat response over SSE. Calls `onEvent` for each parsed event
   * frame; resolves when the stream ends (done / error / abort). Throws on
   * network errors before the stream starts.
   *
   * Uses raw `fetch` because `apiClient` wraps responses as JSON. We still
   * mirror `apiClient`'s auth headers so the backend guards see an
   * identical request shape:
   *   - `x-platform: web|desktop` (web/desktop discrimination)
   *   - `x-organization-id` / `x-organization-slug` from the org store
   *   - On desktop (Tauri), pull the access token from secure storage and
   *     send it as `Authorization: Bearer …` — cookies aren't reliably
   *     available inside the WebView. On web we rely on
   *     `credentials: 'include'` to attach the auth cookie.
   */
  async chatStream(
    request: AssistantChatRequest,
    handlers: StreamHandlers,
  ): Promise<void> {
    await this.streamEvents('/ai/assistant/chat/stream', request, handlers, () =>
      // Counted once the backend has accepted the turn. Never the message
      // text — only shape (length bucket, attachments, new-vs-continued).
      track('benny_message_sent', {
        is_new_session: !request.sessionId,
        has_attachments: !!request.attachments?.length,
        attachment_count: request.attachments?.length ?? 0,
        has_page_context: !!request.pageContext,
        message_length_bucket: bucketMessageLength(request.message),
      }),
    )
  }

  private async streamEvents(
    path: string,
    body: unknown,
    { signal, onEvent }: StreamHandlers,
    onAccepted?: () => void,
  ): Promise<void> {
    const orgState = useOrganizationStore.getState().currentOrganization
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'x-platform': getPlatform(),
    }
    if (orgState?.id) headers['x-organization-id'] = orgState.id
    if (orgState?.slug) headers['x-organization-slug'] = orgState.slug

    if (isTauri()) {
      try {
        const tokens = await desktopSecureStorage.getTokens()
        if (tokens?.accessToken) {
          headers['Authorization'] = `Bearer ${tokens.accessToken}`
        }
      } catch {
        // Best-effort. If secure storage is unavailable the cookie path
        // is still attempted via credentials: 'include'.
      }
    }

    const response = await fetch(`${env.apiUrl}${path}`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
      signal,
    })

    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(
        `Stream request failed (${response.status}): ${text || response.statusText}`,
      )
    }
    if (!response.body) {
      throw new Error('Stream response has no body')
    }

    onAccepted?.()

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .getReader()
    let buffer = ''
    try {
      for (;;) {
        // A stalled stream (provider hung, connection half-dead) used to
        // spin forever with the composer locked. When no frame arrives for
        // STREAM_IDLE_TIMEOUT_MS the turn ends locally as a timeout (C17).
        const { value, done } = await readWithIdleTimeout(reader)
        if (done) break
        buffer += value
        // SSE frames are separated by a blank line. Each frame may have
        // multiple `data:` lines but our backend only emits one per frame.
        let sep
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sep)
          buffer = buffer.slice(sep + 2)
          for (const line of frame.split('\n')) {
            if (!line.startsWith('data: ')) continue
            const payload = line.slice(6)
            if (!payload) continue
            try {
              const parsed = JSON.parse(payload) as AssistantChatStreamEvent
              onEvent(parsed)
              if (parsed.type === 'done' || parsed.type === 'error') return
            } catch {
              // skip malformed frame
            }
          }
        }
      }
    } catch (error) {
      if (error instanceof StreamIdleTimeoutError) {
        onEvent({
          type: 'error',
          code: 'BENNY_PROVIDER_TIMEOUT',
          message: 'Benny stopped responding. Try again.',
        })
        return
      }
      throw error
    } finally {
      // best-effort cancel; reader may already be closed
      try {
        await reader.cancel()
      } catch {
        // ignore
      }
    }
  }

  async listSessions(): Promise<AssistantSession[]> {
    const response = await apiClient<{ data: AssistantSession[] }>(
      '/ai/assistant/sessions',
    )
    return response.data
  }

  async searchSessions(query: string): Promise<AssistantSessionSearchResult[]> {
    if (!query.trim()) return []
    const response = await apiClient<{ data: AssistantSessionSearchResult[] }>(
      `/ai/assistant/sessions/search?q=${encodeURIComponent(query.trim())}`,
    )
    return response.data
  }

  async getMessages(
    sessionId: string,
    options?: { cursor?: string; limit?: number },
  ): Promise<AssistantMessagesPage> {
    const params = new URLSearchParams()
    if (options?.cursor) params.set('cursor', options.cursor)
    if (options?.limit) params.set('limit', String(options.limit))
    const query = params.size > 0 ? `?${params.toString()}` : ''
    const response = await apiClient<{ data: AssistantMessagesPage }>(
      `/ai/assistant/sessions/${sessionId}/messages${query}`,
    )
    return response.data
  }

  async renameSession(
    sessionId: string,
    title: string,
  ): Promise<AssistantSession> {
    const response = await apiClient<{ data: AssistantSession }>(
      `/ai/assistant/sessions/${sessionId}`,
      { method: 'PATCH', body: { title } },
    )
    return response.data
  }

  async deleteSession(sessionId: string): Promise<void> {
    await apiClient(`/ai/assistant/sessions/${sessionId}`, { method: 'DELETE' })
  }

  /** Read a conversation's anchor — the record its canvas defaults to. */
  async getSessionAnchor(
    sessionId: string,
  ): Promise<{ type: string; id: string; name: string } | null> {
    const response = await apiClient<{
      data: { anchor: { type: string; id: string; name: string } | null }
    }>(`/ai/assistant/sessions/${sessionId}/anchor`)
    return response.data.anchor
  }

  /** Set (or clear with null) a conversation's anchor. */
  async updateSessionAnchor(
    sessionId: string,
    anchor: { type: string; id: string; name: string } | null,
  ): Promise<void> {
    await apiClient(`/ai/assistant/sessions/${sessionId}/anchor`, {
      method: 'PATCH',
      body: { anchor },
    })
  }

  /**
   * Read one conversation's persisted Simple Mode canvas customization.
   * Kept separate from listSessions so the (up to 16KB) prefs blob is only
   * ever fetched for the active conversation.
   */
  /** Short-lived download URL for a stored chat attachment (uploader-private). */
  async getChatAttachmentUrl(
    messageId: string,
    index: number,
  ): Promise<{ url: string; fileName: string | null; contentType: string }> {
    const response = await apiClient<{
      data: { url: string; fileName: string | null; contentType: string }
    }>(`/ai/assistant/messages/${messageId}/attachments/${index}/url`)
    return response.data
  }

  async getSessionCanvas(
    sessionId: string,
  ): Promise<Record<string, unknown> | null> {
    const response = await apiClient<{
      data: { canvas: Record<string, unknown> | null }
    }>(`/ai/assistant/sessions/${sessionId}/canvas`)
    return response.data.canvas
  }

  /** Persist Simple Mode canvas customization for one conversation. */
  async updateSessionCanvas(sessionId: string, canvas: object): Promise<void> {
    await apiClient(`/ai/assistant/sessions/${sessionId}/canvas`, {
      method: 'PATCH',
      body: { canvas },
    })
  }
}

export const assistantApi = createSingletonApi(AssistantApi)
